import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetChecklistItemByIdQuery,
  useUpdateResearcherChecklistItemMutation,
  useSubmitItemForReviewMutation,
  useGetItemCommentsQuery,
  useAddItemCommentMutation,
  useApproveChecklistItemMutation,
  useRejectChecklistItemMutation,
  useGetResearcherReportsForProgramQuery
} from "../../utils/api/endpoints/checklists/researcherChecklistApi";
import { reportsApi } from "../../utils/api/endpoints/reportsApi";
import { FiArrowLeft, FiSave, FiSend, FiUpload, FiX, FiMessageSquare, FiCheckCircle, FiXCircle, FiAlertCircle, FiFileText, FiLock, FiShield, FiUser } from "react-icons/fi";
import toast from "react-hot-toast";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import type { CTBReport } from "../../utils/api/endpoints/reports/parseReports";
import { ConfirmationModal } from "../../components/checklists/ConfirmationModal";

const STATUS_OPTIONS = [
  { value: "NOT_STARTED", label: "Not Started" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "NOT_APPLICABLE", label: "Not Applicable" },
  { value: "FAILED", label: "Failed" }
];

// Compact badge helper functions
const getStatusBadge = (status: string) => {
  const badges: Record<string, { bg: string; text: string; border: string; label: string }> = {
    PENDING_REVIEW: { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200", label: "Pending" },
    COMPLETED: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", label: "Completed" },
    REJECTED: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", label: "Rejected" },
    IN_PROGRESS: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", label: "In Progress" },
    NOT_STARTED: { bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200", label: "Not Started" },
    NOT_APPLICABLE: { bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200", label: "N/A" },
    FAILED: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", label: "Failed" }
  };

  const badge = badges[status] || badges.NOT_STARTED;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${badge.bg} ${badge.text} ${badge.border}`}>
      {badge.label}
    </span>
  );
};

const ASVS_LEVELS = ["L1", "L2", "L3"];

const EMPTY_ARRAY: any[] = [];

const ChecklistItemWorkspace = () => {
  const { itemId } = useParams<{ itemId: string }>();
  const [searchParams] = useSearchParams();
  const runId = searchParams.get("runId");
  const navigate = useNavigate();
  const { role } = useUserCredentials();

  // Check if user is QA/ADMIN
  const isReviewer = role && [UserRole.ADMIN, UserRole.QA, UserRole.SUB_ADMIN].includes(role);

  // Fetch the item directly by ID
  const { data: itemData, isLoading: isLoadingItem } = useGetChecklistItemByIdQuery(Number(itemId));

  // Get comments
  const { data: commentsData, refetch: refetchComments } = useGetItemCommentsQuery(Number(itemId), {
    pollingInterval: 5000 // Poll every 5 seconds for new comments
  });

  const [updateItem, { isLoading: isUpdating }] = useUpdateResearcherChecklistItemMutation();
  const [submitForReview, { isLoading: isSubmitting }] = useSubmitItemForReviewMutation();
  const [addComment, { isLoading: isAddingComment }] = useAddItemCommentMutation();
  const [approveItem, { isLoading: isApproving }] = useApproveChecklistItemMutation();
  const [rejectItem, { isLoading: isRejecting }] = useRejectChecklistItemMutation();

  const [fetchReport] = reportsApi.useLazyGetReportQuery();

  // Form state
  const [status, setStatus] = useState("");
  const [actualEvidence, setActualEvidence] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<string[]>([]);
  const [commentText, setCommentText] = useState("");
  const [bugFound, setBugFound] = useState(false);
  const [selectedReportIds, setSelectedReportIds] = useState<number[]>([]);
  const [linkedReports, setLinkedReports] = useState<CTBReport[]>([]);
  const [isLoadingLinkedReports, setIsLoadingLinkedReports] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: "info" | "danger" | "warning" | "success";
    confirmLabel?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "info",
  });

  const currentItem = itemData?.data;

  // Get program ID from the current item (normalized by backend)
  const programId = currentItem?.program_id;
  const companyName = currentItem?.company_name;

  // Authorization check for workspace actions
  const canTakeAction = useMemo(() => {
    if (!currentItem) return false;
    // Admins/QA/Sub-Admins can always take action to map reports
    if (isReviewer) return true;
    return (currentItem.is_assigned_to_me || !currentItem.is_assigned);
  }, [currentItem, isReviewer]);

  // Fetch reports for this program/run (Admins see all; researchers only see theirs)
  const { data: reportsData } = useGetResearcherReportsForProgramQuery(
    { programId: programId!, runId: runId ? Number(runId) : null },
    { skip: !programId }
  );

  const researcherReports = useMemo(() => reportsData?.data || EMPTY_ARRAY, [reportsData?.data]);
  
  // Split results into Admin and Researcher categories
  const { adminResults, researcherResults } = useMemo(() => {
    const results = currentItem?.all_results || [];
    const adminActions: any[] = [];
    const researcherActions: any[] = [];

    const ADMIN_ROLES = [UserRole.ADMIN, UserRole.QA, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER];

    results.forEach((res: any) => {
      const isSystemAdmin = res.user_id === 0 || res.researcher?.username === "system_admin";
      const isAdminRole = res.researcher?.role && ADMIN_ROLES.includes(res.researcher.role);

      if (isSystemAdmin || isAdminRole) {
        adminActions.push(res);
      } else {
        researcherActions.push(res);
      }
    });

    return { 
      adminResults: adminActions, 
      researcherResults: researcherActions 
    };
  }, [currentItem?.all_results]);

  // Initialize form when item loads
  useEffect(() => {
    if (currentItem) {
      // If reviewer, check first researcher result in all_results, otherwise fallback to my_result
      const ADMIN_ROLES = [UserRole.ADMIN, UserRole.QA, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER];
      const firstResearcherResult = currentItem.all_results?.find((res: any) => {
        const isSystemAdmin = res.user_id === 0 || res.researcher?.username === "system_admin";
        const isAdminRole = res.researcher?.role && ADMIN_ROLES.includes(res.researcher.role);
        return !isSystemAdmin && !isAdminRole;
      });

      const myResult = isReviewer ? (firstResearcherResult || currentItem.my_result) : currentItem.my_result;
      
      const currentStatus = myResult ? myResult.status : "NOT_STARTED";
      if (status !== currentStatus) setStatus(currentStatus);
      
      const newActualEvidence = myResult ? (myResult.actual_evidence || "") : "";
      if (actualEvidence !== newActualEvidence) setActualEvidence(newActualEvidence);
      
      const newNotes = myResult ? (myResult.notes || "") : "";
      if (notes !== newNotes) setNotes(newNotes);
      
      const newExistingFiles = myResult ? (myResult.evidence_files || EMPTY_ARRAY) : EMPTY_ARRAY;
      if (JSON.stringify(existingFiles) !== JSON.stringify(newExistingFiles)) {
        setExistingFiles(newExistingFiles);
      }
      
      const newBugFound = myResult ? (myResult.bug_found || false) : false;
      if (bugFound !== newBugFound) setBugFound(newBugFound);

      // Initialize this researcher's linked reports (multi-select)
      const myLinkedIds = myResult ? (myResult.linked_report_ids || EMPTY_ARRAY) : EMPTY_ARRAY;
      
      // Only update if IDs actually changed to avoid infinite loops
      const currentIdsSorted = [...selectedReportIds].sort().join(",");
      const newIdsSorted = [...myLinkedIds].sort().join(",");
      
      if (currentIdsSorted !== newIdsSorted) {
        setSelectedReportIds(myLinkedIds);
      }
    }
  }, [currentItem, isReviewer]);

  // For reviewers (QA/Admin), load full report details for linked reports
  useEffect(() => {
    if (!isReviewer || !currentItem?.linked_report_ids || currentItem.linked_report_ids.length === 0) {
      setLinkedReports([]);
      return;
    }

    let cancelled = false;

    const loadLinkedReports = async () => {
      try {
        setIsLoadingLinkedReports(true);
        const results: CTBReport[] = [];

        for (const reportId of currentItem.linked_report_ids || []) {
          try {
            const report = await fetchReport(reportId).unwrap();
            results.push(report);
          } catch (error) {
            console.error("Failed to load linked report", reportId, error);
          }
        }

        if (!cancelled) {
          setLinkedReports(results);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingLinkedReports(false);
        }
      }
    };

    loadLinkedReports();

    return () => {
      cancelled = true;
    };
  }, [isReviewer, currentItem, fetchReport]);

  // Auto-scroll to bottom of comments
  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [commentsData]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles([...files, ...Array.from(e.target.files)]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleRemoveExistingFile = (url: string) => {
    setExistingFiles(existingFiles.filter((f) => f !== url));
  };

  const handleSave = async () => {
    try {
      console.log("=== FRONTEND: Preparing to save ===");
      console.log("Status:", status);
      console.log("Actual Evidence:", actualEvidence);
      console.log("Notes:", notes);
      console.log("Existing Files:", existingFiles);
      console.log("New Files:", files);
      console.log("Bug Found:", bugFound);
      console.log("Selected Report IDs:", selectedReportIds);
      console.log("Version:", currentItem?.version);

      const formData = new FormData();
      formData.append("itemId", itemId!);
      formData.append("status", status);
      formData.append("actualEvidence", actualEvidence);
      formData.append("notes", notes);
      formData.append("existingEvidenceFiles", JSON.stringify(existingFiles));
      formData.append("bugFound", String(bugFound));
      formData.append("linkedReportIds", JSON.stringify(selectedReportIds));

      // Include version for optimistic locking
      if (currentItem?.version !== undefined) {
        formData.append("version", String(currentItem.version));
      }

      files.forEach((file) => {
        formData.append("evidence", file);
      });

      console.log("=== FRONTEND: FormData entries ===");
      for (let pair of formData.entries()) {
        console.log(pair[0], typeof pair[1] === 'string' ? pair[1] : 'File');
      }

      await updateItem(formData).unwrap();
      toast.success("Item updated successfully");
      setFiles([]);
    } catch (error: any) {
      const errorMessage = error?.data?.message || "Failed to update item";

      // Check for concurrent edit conflict
      if (errorMessage.includes("Concurrent edit detected")) {
        toast.error(
          "This item was modified by another user. The page will refresh to show the latest version.",
          { duration: 5000 }
        );
        // Refresh the item data after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleSubmitForReview = async () => {
    try {
      // First, save any pending changes (evidence, notes, files)
      console.log("=== FRONTEND: Submitting for review ===");
      console.log("Status:", status);
      console.log("Actual Evidence:", actualEvidence);
      console.log("Notes:", notes);
      console.log("Existing Files:", existingFiles);
      console.log("New Files:", files);
      console.log("Bug Found:", bugFound);
      console.log("Selected Report IDs:", selectedReportIds);

      const formData = new FormData();
      formData.append("itemId", itemId!);
      formData.append("status", "PENDING_REVIEW");
      formData.append("actualEvidence", actualEvidence);
      formData.append("notes", notes);
      formData.append("existingEvidenceFiles", JSON.stringify(existingFiles));
      formData.append("bugFound", String(bugFound));
      formData.append("linkedReportIds", JSON.stringify(selectedReportIds));

      files.forEach((file) => {
        formData.append("evidence", file);
      });

      // Save the data first
      await updateItem(formData).unwrap();

      // Then submit for review
      await submitForReview({ itemId: Number(itemId) }).unwrap();
      toast.success("Item submitted for review");
      setFiles([]);
      refetchComments();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to submit for review");
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;

    try {
      await addComment({
        itemId: Number(itemId),
        commentText: commentText.trim()
      }).unwrap();
      setCommentText("");
      toast.success("Comment added");
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to add comment");
    }
  };

  const handleApprove = async () => {
    setConfirmModal({
      isOpen: true,
      title: "Approve Item",
      message: "Are you sure you want to approve this item?",
      type: "success",
      confirmLabel: "Approve",
      onConfirm: async () => {
        try {
          await approveItem({
            itemId: Number(itemId),
            comment: "Item approved"
          }).unwrap();

          toast.success("Item approved successfully");
          const backUrl = isReviewer 
            ? "/dashboard/checklist-review"
            : `/dashboard/researcher-checklists/programs/${programId}${runId ? `?runId=${runId}` : ""}`;
          navigate(backUrl);
        } catch (error: any) {
          toast.error(error?.data?.message || "Failed to approve item");
        }
      }
    });
  };

  const handleReject = async () => {
    const reason = window.prompt("Please provide a reason for rejection:");
    if (!reason || !reason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    try {
      await rejectItem({
        itemId: Number(itemId),
        comment: reason.trim()
      }).unwrap();

      toast.success("Item rejected");
      refetchComments();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to reject item");
    }
  };

  if (isLoadingItem || !currentItem) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const comments = commentsData?.data || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="px-4 pt-4">
          <div className="max-w-[1600px] mx-auto flex items-center gap-4">
            {/* Back Button */}
            <button
              onClick={() => {
                const backUrl = isReviewer
                  ? "/dashboard/checklist-review"
                  : `/dashboard/researcher-checklists/programs/${programId}${runId ? `?runId=${runId}` : ""}`;
                navigate(backUrl);
              }}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              title="Back to items"
            >
              <FiArrowLeft className="w-5 h-5 text-gray-400" />
            </button>

            <div className="rounded-2xl px-6 py-4 flex-1" style={{ backgroundColor: '#011F3E' }}>
            <div className="flex items-center justify-between">
              {/* Left Side - Title and Description */}
              <div className="flex items-center gap-4">
                <div className="flex flex-col">
                  {/* Breadcrumb / Company Context */}
                  <div className="flex items-center gap-2 text-xs font-semibold text-blue-300/80 mb-1">
                    <span className="uppercase tracking-wider">{companyName || "Company"}</span>
                    <span>•</span>
                    <span className="text-white/60">
                      {currentItem.run_title || (runId ? `Run #${runId}` : "Checklist")}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-lg font-bold text-white break-all">{currentItem.title}</h1>
                    {currentItem.requirement_id && (
                      <span className="inline-flex items-center gap-1 rounded bg-white/10 px-2 py-0.5 text-[10px] font-mono font-bold text-blue-200 border border-blue-400/30">
                        {currentItem.requirement_id}
                      </span>
                    )}
                    {currentItem.framework === "ASVS" && (
                      <div className="flex gap-1">
                        {ASVS_LEVELS.map(level => {
                          const key = level.toLowerCase() as "l1" | "l2" | "l3";
                          if (!(currentItem as any)[key]) return null;
                          return (
                            <span key={level} className="inline-flex items-center rounded bg-[#58CC02]/20 px-2 py-0.5 text-[9px] font-black text-[#58CC02] uppercase border border-[#58CC02]/30">
                              {level}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Target Badges (Web/API) */}
                    <div className="flex gap-1">
                      {currentItem.is_web && (
                        <span className="inline-flex items-center rounded bg-blue-500/20 px-2 py-0.5 text-[9px] font-bold text-blue-300 uppercase border border-blue-500/30">
                          Web
                        </span>
                      )}
                      {currentItem.is_api && (
                        <span className="inline-flex items-center rounded bg-purple-500/20 px-2 py-0.5 text-[9px] font-bold text-purple-300 uppercase border border-purple-500/30">
                          API
                        </span>
                      )}
                    </div>
                    {/* Status Badge */}
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: 
                      currentItem.status === "COMPLETED" ? "#58CC02" : 
                      currentItem.status === "PENDING_REVIEW" ? "#FFA500" :
                      currentItem.status === "REJECTED" ? "#FF4B4B" :
                      currentItem.status === "FAILED" ? "#FF4B4B" : "#4B4B4B"
                    }}>
                      {currentItem.status === "IN_PROGRESS" ? "In Progress" :
                       currentItem.status === "PENDING_REVIEW" ? "Pending Review" :
                       currentItem.status === "COMPLETED" ? "Completed" :
                       currentItem.status === "NOT_STARTED" ? "Not Started" :
                       currentItem.status === "FAILED" ? "Failed" : 
                       currentItem.status === "REJECTED" ? "Rejected" : currentItem.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Side - Action Buttons */}
              <div className="flex items-center gap-3">
                {/* Researcher Actions */}
                {!isReviewer && (currentItem.is_assigned_to_me || !currentItem.is_assigned) && (
                  <button
                    onClick={handleSave}
                    disabled={isUpdating}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-black text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-emerald-500/20"
                    style={{ backgroundColor: '#58CC02' }}
                  >
                    <FiCheckCircle className="w-5 h-5" />
                    {isUpdating ? "Finalizing..." : "Save & Complete Item"}
                  </button>
                )}
                
                {!isReviewer && !currentItem.is_assigned_to_me && currentItem.is_assigned && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg border border-amber-200 text-xs font-bold">
                    <FiLock className="w-3.5 h-3.5" />
                    Read-Only Access
                  </div>
                )}

                {/* QA/ADMIN Actions */}
                {isReviewer && currentItem.status === "PENDING_REVIEW" && (
                  <>
                    <button
                      onClick={handleReject}
                      disabled={isRejecting}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <FiXCircle className="w-4 h-4" />
                      {isRejecting ? "Rejecting..." : "Reject"}
                    </button>

                    <button
                      onClick={handleApprove}
                      disabled={isApproving}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      style={{ backgroundColor: '#58CC02' }}
                    >
                      <FiCheckCircle className="w-4 h-4" />
                      {isApproving ? "Approving..." : "Approve"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Item Details & Work Area */}
          <div className="lg:col-span-2 space-y-3">
            {/* Test Case Details Card */}
            <div className="border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              {/* Header with light blue background */}
              <div className="bg-gradient-to-r from-blue-50/80 to-slate-50 px-6 py-5">
                <h2 className="text-lg font-bold text-gray-900">Test Case Details</h2>
              </div>

              {/* Content with white background */}
              <div className="bg-white px-6 py-5 space-y-6">
                {currentItem.description && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-blue-700 mb-1">Description</h3>
                      <p className="text-sm text-gray-500 break-all">{currentItem.description}</p>
                    </div>
                  </div>
                )}

                {currentItem.test_objective && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="9" strokeWidth={2} />
                        <circle cx="12" cy="12" r="5" strokeWidth={2} />
                        <circle cx="12" cy="12" r="1" fill="currentColor" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-blue-700 mb-1">Test Objective</h3>
                      <p className="text-sm text-gray-500 break-all">{currentItem.test_objective}</p>
                    </div>
                  </div>
                )}

                {currentItem.expected_evidence && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-green-700 mb-1">Expected Evidence</h3>
                      <p className="text-sm text-gray-500 break-all">{currentItem.expected_evidence}</p>
                    </div>
                  </div>
                )}

                {currentItem.references && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-purple-700 mb-1">References</h3>
                      <p className="text-sm text-gray-500 break-all">{currentItem.references}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Admin Actions Section */}
            {adminResults.length > 0 && (
              <div className="bg-white border border-blue-200 rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-slate-50 px-4 py-3 border-b border-blue-100 flex items-center justify-between">
                  <h2 className="text-sm font-bold text-blue-900 flex items-center gap-2">
                    <FiShield className="w-4 h-4 text-blue-600" />
                    Admin Actions
                  </h2>
                  <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
                    {adminResults.length} Administrative Records
                  </span>
                </div>
                <div className="p-4 space-y-4">
                  {adminResults.map((res: any) => (
                    <div key={res.result_id} className="p-3 rounded-xl border border-blue-50 bg-blue-50/30">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-[10px] font-bold text-white uppercase">
                            {(res.researcher?.display_name || res.researcher?.username || "SA").substring(0, 2)}
                          </div>
                          <span className="text-xs font-bold text-blue-900">
                            {res.researcher?.display_name || res.researcher?.username}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase border ${
                            res.status === "COMPLETED" ? "bg-green-50 text-green-700 border-green-200" :
                            res.status === "FAILED" ? "bg-red-50 text-red-700 border-red-200" :
                            res.status === "NOT_APPLICABLE" ? "bg-gray-50 text-gray-700 border-gray-200" :
                            "bg-blue-50 text-blue-700 border-blue-200"
                          }`}>
                            {res.status === "COMPLETED" ? "PASSED" : 
                             res.status === "FAILED" ? "FAILED" : 
                             res.status === "NOT_APPLICABLE" ? "N/A" : res.status}
                          </span>
                          <span className="text-[10px] text-blue-400">
                            {new Date(res.tested_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      
                      {res.actual_evidence && (
                        <div className="mb-2">
                          <span className="text-[9px] font-bold text-blue-400 uppercase tracking-widest block mb-1">Evidence</span>
                          <p className="text-[11px] text-blue-600 bg-white/60 p-2 rounded border border-blue-50 italic">
                            {res.actual_evidence}
                          </p>
                        </div>
                      )}

                      {res.notes && (
                        <div className="mb-2">
                          <span className="text-[9px] font-bold text-blue-400 uppercase tracking-widest block mb-1">Internal Notes</span>
                          <p className="text-[11px] text-blue-600">
                            {res.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Other Researchers' Results Section */}
            {researcherResults.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <FiUser className="w-4 h-4 text-purple-600" />
                    Other Researchers' Tests
                  </h2>
                  <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-100">
                    {researcherResults.length} Total Results
                  </span>
                </div>
                <div className="p-4 space-y-4">
                  {researcherResults.map((res: any) => (
                    <div key={res.result_id} className="p-3 rounded-xl border border-gray-100 bg-slate-50/50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-[10px] font-bold text-white uppercase">
                            {(res.researcher?.display_name || res.researcher?.username || "??").substring(0, 2)}
                          </div>
                          <span className="text-xs font-bold text-gray-900">
                            {res.researcher?.display_name || res.researcher?.username}
                          </span>
                          {res.user_id === currentItem.my_result?.user_id && (
                            <span className="text-[10px] font-black text-blue-600 uppercase tracking-tighter">(You)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase border ${
                            res.status === "COMPLETED" ? "bg-green-50 text-green-700 border-green-200" :
                            res.status === "FAILED" ? "bg-red-50 text-red-700 border-red-200" :
                            res.status === "NOT_APPLICABLE" ? "bg-gray-50 text-gray-700 border-gray-200" :
                            "bg-blue-50 text-blue-700 border-blue-200"
                          }`}>
                            {res.status === "COMPLETED" ? "PASSED" : 
                             res.status === "FAILED" ? "FAILED" : 
                             res.status === "NOT_APPLICABLE" ? "N/A" : res.status}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            {new Date(res.tested_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      
                      {res.actual_evidence && (
                        <div className="mb-2">
                          <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Evidence</span>
                          <p className="text-[11px] text-gray-600 bg-white p-2 rounded border border-gray-100 italic">
                            {res.actual_evidence}
                          </p>
                        </div>
                      )}

                      {res.notes && (
                        <div className="mb-2">
                          <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Researcher Notes</span>
                          <p className="text-[11px] text-gray-600">
                            {res.notes}
                          </p>
                        </div>
                      )}

                      {res.bug_found && (
                        <div className="mt-2 pt-2 border-t border-gray-100 flex flex-col gap-2">
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-red-600">
                            <FiAlertCircle className="w-3 h-3" />
                            Bug Reported
                          </div>
                          {res.linked_reports_metadata?.length > 0 && (
                            <div className="space-y-1">
                              {res.linked_reports_metadata.map((report: any) => 
                                  report.is_restricted_for_business ? (
                                    <div className="flex flex-col p-2 bg-gray-50 border border-gray-200 rounded text-[10px] shadow-sm cursor-help transition-all hover:bg-gray-100" title="Report is undergoing administrative review and is not yet available for viewing.">
                                      <div className="flex items-center justify-between gap-1 mb-1">
                                        <div className="flex items-center gap-1">
                                          <FiLock className="w-2.5 h-2.5 text-gray-400" />
                                          <span className="font-black text-gray-500">#{report.report_id}</span>
                                        </div>
                                        <span className="px-1 rounded-[4px] font-black text-[8px] uppercase tracking-tighter bg-gray-200 text-gray-600">
                                          In Review
                                        </span>
                                      </div>
                                      <span className="text-gray-400 font-bold italic">Administrative Review in Progress</span>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/dashboard/reports/${report.report_id}`);
                                      }}
                                      className="flex flex-col p-1.5 bg-white border border-red-100 rounded text-[10px] shadow-sm transition-all hover:border-red-300 hover:shadow-md text-left group/card"
                                    >
                                      <div className="flex items-center justify-between gap-1 mb-0.5">
                                        <span className="font-black text-red-700 group-hover/card:text-red-600 transition-colors">#{report.report_id}</span>
                                        <span className={`px-1 rounded-[4px] font-black text-[8px] uppercase tracking-tighter ${
                                          report.severity_category === 'HIGH' || report.severity_category === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                                          report.severity_category === 'MEDIUM' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                                        }`}>
                                          {report.severity_category || 'N/A'}
                                        </span>
                                      </div>
                                      <span className="text-gray-600 font-medium truncate group-hover/card:text-blue-600 transition-colors">{report.report_title}</span>
                                    </button>
                                  )
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Professional Status Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-4 py-3 border-b border-gray-200">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                  Test Outcome
                </h2>
              </div>
              <div className="p-4">
                {isReviewer && !canTakeAction ? (
                  <div className="w-full px-3 py-2.5 text-sm font-semibold border border-gray-300 rounded-lg bg-gradient-to-r from-gray-50 to-slate-50">
                    {status === "COMPLETED" ? "Passed" : 
                     status === "FAILED" ? "Failed" : 
                     status === "NOT_APPLICABLE" ? "N/A" : status}
                  </div>
                ) : (
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm font-black border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:border-blue-300 cursor-pointer uppercase tracking-tight"
                    disabled={!canTakeAction}
                  >
                    <option value="NOT_STARTED">Pending Result</option>
                    <option value="COMPLETED">✅ Passed</option>
                    <option value="FAILED">❌ Failed</option>
                    <option value="NOT_APPLICABLE">➖ Not Applicable</option>
                  </select>
                )}
                {!isReviewer && (status === "PENDING_REVIEW" || (!!currentItem.reviewed_at && currentItem.status !== "IN_PROGRESS")) && (
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Status is locked while under review or completed
                  </p>
                )}
                {isReviewer && (
                  <p className="text-xs text-blue-600 mt-2 flex items-center gap-1.5 font-medium">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Read-only for Admin/QA
                  </p>
                )}
              </div>
            </div>

            {/* Professional Bug Found Section - Visible to Researchers and Reviewers */}
            {(true) && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-4 py-3 border-b border-gray-200">
                  <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                    Test Result
                  </h2>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        setBugFound(true);
                      }}
                      disabled={!canTakeAction}
                      className={`px-4 py-3 rounded-lg border-2 transition-all font-semibold text-sm flex items-center justify-center gap-2 ${
                        bugFound
                          ? "border-red-500 bg-gradient-to-br from-red-50 to-red-100 text-red-700 shadow-sm"
                          : "border-gray-300 bg-white text-gray-700 hover:border-red-400 hover:bg-red-50"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <FiAlertCircle className="w-4 h-4" />
                      Bug Found
                    </button>
                    <button
                      onClick={() => {
                        setBugFound(false);
                        setSelectedReportIds([]);
                      }}
                      disabled={!canTakeAction}
                      className={`px-4 py-3 rounded-lg border-2 transition-all font-semibold text-sm flex items-center justify-center gap-2 ${
                        !bugFound
                          ? "border-green-500 bg-gradient-to-br from-green-50 to-green-100 text-green-700 shadow-sm"
                          : "border-gray-300 bg-white text-gray-700 hover:border-green-400 hover:bg-green-50"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <FiCheckCircle className="w-4 h-4" />
                      No Bug Found
                    </button>
                  </div>

                  {/* Report Selection - Only show if bug found */}
                  {bugFound && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-2">
                          Link to Reports (Optional)
                        </label>
                        <select
                          multiple
                          value={selectedReportIds.map(String)}
                          onChange={(e) => {
                            const options = Array.from(e.target.selectedOptions);
                            const values = options.map(o => Number(o.value)).filter(v => !isNaN(v));
                            setSelectedReportIds(values);
                          }}
                          disabled={!canTakeAction}
                          className="w-full px-3 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-white hover:border-gray-400 min-h-[80px]"
                        >
                          {researcherReports.map((report) => (
                            <option key={report.report_id} value={report.report_id}>
                              #{report.report_id} - {report.report_title} ({report.severity_category || "N/A"})
                            </option>
                          ))}
                        </select>
                        {researcherReports.length === 0 && (
                          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                            <FiFileText className="w-3.5 h-3.5" />
                            No reports found for this program. You can still provide evidence below.
                          </p>
                        )}
                      </div>

                      {/* Table showing this researcher's linked reports for this test case */}
                      {selectedReportIds.length > 0 && (
                        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50/60">
                          <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-700">
                              Linked Reports for this Test Case (You)
                            </span>
                            <span className="text-[11px] text-gray-500">
                              {selectedReportIds.length} selected
                            </span>
                          </div>
                          <div className="max-h-40 overflow-auto">
                            <table className="min-w-full text-xs">
                              <thead className="bg-gray-100 border-b border-gray-200">
                                <tr>
                                  <th className="px-3 py-1 text-left font-semibold text-gray-600">ID</th>
                                  <th className="px-3 py-1 text-left font-semibold text-gray-600">Title</th>
                                  <th className="px-3 py-1 text-left font-semibold text-gray-600">Severity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedReportIds.map(id => {
                                  const report = researcherReports.find(r => r.report_id === id);
                                  if (!report) return null;
                                  return (
                                    <tr key={id} className="border-b border-gray-100 last:border-0">
                                      <td className="px-3 py-1 text-gray-800 font-semibold whitespace-nowrap flex items-center gap-1.5">
                                        {report.is_restricted_for_business && <FiLock className="w-3 h-3 text-gray-400" />}
                                        #{report.report_id}
                                      </td>
                                      <td className={`px-3 py-1 truncate max-w-[220px] ${report.is_restricted_for_business ? 'text-gray-400 italic font-medium' : 'text-gray-700'}`}>
                                        {report.report_title}
                                        {report.is_restricted_for_business && (
                                          <span className="ml-2 inline-block px-1.5 py-0.5 bg-gray-100 text-[8px] font-black uppercase text-gray-500 rounded border border-gray-200">
                                            Admin Review
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-1 text-gray-600 whitespace-nowrap">{report.severity_category || "N/A"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Display bug found info for reviewers */}
            {isReviewer && currentItem.bug_found && (
              <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-300 rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 text-red-700 font-bold text-sm mb-3">
                  <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                    <FiAlertCircle className="w-4 h-4 text-white" />
                  </div>
                  Bug Found During Testing
                </div>
                {currentItem.linked_report_ids && currentItem.linked_report_ids.length > 0 && (
                  <div className="text-sm text-red-700">
                    <span className="font-bold">Linked Reports:</span>
                    <div className="mt-2 space-y-2">
                      {linkedReports.length > 0 ? (
                        linkedReports.map(report => (
                          <button
                            key={report.id}
                            type="button"
                            onClick={() => navigate(`/dashboard/reports/${report.id}`)}
                            className="w-full text-left rounded-lg bg-white/90 border border-red-200 px-3 py-2 shadow-sm hover:border-red-400 hover:bg-white transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-red-800 truncate">
                                  #{report.id} - {report.title}
                                </p>
                                <p className="mt-0.5 text-[11px] text-red-700/80 truncate">
                                  {report.severityCategory || "Severity: N/A"}
                                  {report.state ? ` - State: ${report.state}` : ""}
                                </p>
                              </div>
                              <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700 border border-red-200">
                                View Report
                              </span>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {currentItem.linked_report_ids.map(reportId => (
                            <span
                              key={reportId}
                              className="inline-flex items-center px-2.5 py-1 rounded-lg bg-white border-2 border-red-400 text-red-700 font-bold text-xs shadow-sm"
                            >
                              #{reportId}
                            </span>
                          ))}
                        </div>
                      )}

                      {isLoadingLinkedReports && linkedReports.length === 0 && (
                        <p className="text-[11px] text-red-600/80 mt-1">
                          Loading linked report details...
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Compact Evidence Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-2">
                📝 Actual Evidence {isReviewer && <span className="text-blue-600">(Provided by Researcher)</span>}
              </label>
              {isReviewer ? (
                <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-yellow-50 min-h-[120px] whitespace-pre-wrap">
                  {actualEvidence || <span className="text-gray-400 italic">No evidence provided yet</span>}
                </div>
              ) : (
                <textarea
                  value={actualEvidence}
                  onChange={(e) => setActualEvidence(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  placeholder="Describe your findings in detail... Include steps to reproduce, impact, and any relevant observations."
                  disabled={!canTakeAction}
                />
              )}
              <p className="text-xs text-gray-500 mt-1.5">
                {actualEvidence.length} characters
              </p>
            </div>

            {/* Compact File Upload Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-2">
                📎 Evidence Files {isReviewer && <span className="text-blue-600">(Uploaded by Researcher)</span>}
              </label>

              {!isReviewer && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={!canTakeAction}
                  />

                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 flex flex-col items-center justify-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!canTakeAction}
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <FiUpload className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="font-medium">Click to upload files</span>
                    <span className="text-xs text-gray-500">or drag and drop</span>
                  </button>
                </>
              )}

              {/* Existing Files */}
              {existingFiles.length > 0 ? (
                <div className={`${!isReviewer ? 'mt-2' : ''} space-y-1.5`}>
                  <p className="text-xs font-medium text-gray-700">
                    {isReviewer ? 'Files:' : 'Uploaded Files:'}
                  </p>
                  {existingFiles.map((url, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2 flex-1">
                        <svg className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-purple-700 hover:text-purple-900 hover:underline truncate flex-1 font-medium"
                        >
                          {url.split("/").pop()}
                        </a>
                      </div>
                      {!isReviewer && (
                        <button
                          onClick={() => handleRemoveExistingFile(url)}
                          className="ml-2 p-1 hover:bg-purple-100 rounded transition-colors"
                          disabled={!canTakeAction}
                        >
                          <FiX className="w-3.5 h-3.5 text-purple-600" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : isReviewer ? (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-center">
                  <p className="text-sm text-gray-500 italic">No files uploaded</p>
                </div>
              ) : null}

              {/* New Files - Only for Researchers */}
              {!isReviewer && files.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  <p className="text-xs font-medium text-gray-700">New Files:</p>
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                      <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className="ml-2 p-1 hover:bg-blue-100 rounded"
                      >
                        <FiX className="w-3.5 h-3.5 text-gray-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Compact Notes */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-2">
                📋 Notes {isReviewer && <span className="text-blue-600">(From Researcher)</span>}
              </label>
              {isReviewer ? (
                <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 min-h-[60px] whitespace-pre-wrap">
                  {notes || <span className="text-gray-400 italic">No notes added</span>}
                </div>
              ) : (
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  placeholder="Add any additional notes..."
                  disabled={!canTakeAction}
                />
              )}
            </div>
          </div>

          {/* Right: Comments Section */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-3xl sticky top-16 max-h-[calc(100vh-80px)] flex flex-col">
              {/* Header */}
              <div className="px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h2 className="text-2xl font-bold text-gray-900">Comments</h2>
                </div>
                <span className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-sm font-medium text-gray-600">
                  {comments.length}
                </span>
              </div>

              {/* Divider - full width line */}
              <div className="border-t border-gray-200"></div>

              {/* Comments List */}
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                {comments.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No comments yet</p>
                ) : (
                  comments.map((comment) => {
                    const isRejection = comment.comment_type === "REJECTION";
                    const isApproval = comment.comment_type === "APPROVAL";

                    // Avatar color: orange for rejection, blue for others
                    const avatarBgColor = isRejection ? '#F97316' : '#3B82F6';

                    // Comment box background - very light
                    const bgColor = isRejection ? '#FEF3E2' : isApproval ? '#ECFDF5' : '#EFF6FF';

                    // Text color inside comment box
                    const textColor = isRejection ? '#9A3412' : isApproval ? '#166534' : '#1E40AF';

                    // Badge colors
                    const badgeBg = isRejection ? '#FED7AA' : isApproval ? '#BBF7D0' : '#BFDBFE';
                    const badgeText = isRejection ? '#C2410C' : isApproval ? '#15803D' : '#1D4ED8';

                    return (
                      <div key={comment.comment_id} className="flex gap-4">
                        {/* Avatar */}
                        <div
                          className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                          style={{ backgroundColor: avatarBgColor }}
                        >
                          {(comment.author.display_name || comment.author.username).substring(0, 2).toUpperCase()}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {/* Name and Date Row - name left, date far right */}
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold text-gray-900">
                              {comment.author.display_name || comment.author.username}
                            </span>
                            <span className="text-sm text-gray-400">
                              {(() => {
                                try {
                                  const dateStr = comment.created_at || (comment as any).createdAt;
                                  if (!dateStr) return "Just now";
                                  const date = new Date(dateStr);
                                  if (isNaN(date.getTime())) return "Just now";
                                  return date.toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  });
                                } catch (e) {
                                  return "Just now";
                                }
                              })()}
                            </span>
                          </div>

                          {/* Comment Box */}
                          <div
                            className="rounded-xl px-4 py-4"
                            style={{ backgroundColor: bgColor }}
                          >
                            <p
                              className="text-sm whitespace-pre-wrap leading-relaxed"
                              style={{ color: textColor }}
                            >
                              {comment.comment_text}
                            </p>

                            {/* Badge */}
                            {comment.comment_type !== "COMMENT" && (
                              <span
                                className="inline-block mt-4 px-3 py-1 text-xs font-bold rounded-md"
                                style={{ backgroundColor: badgeBg, color: badgeText }}
                              >
                                {comment.comment_type === "RESUBMISSION" ? "SUBMISSION" : comment.comment_type}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={commentsEndRef} />
              </div>

              {/* Add Comment */}
              <div className="px-6 py-6 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                    placeholder="Add a comment..."
                    className="flex-1 px-5 py-3 text-base text-gray-700 placeholder-gray-400 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={isAddingComment || !commentText.trim()}
                    className="w-12 h-12 flex items-center justify-center rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90 shadow-md"
                    style={{ backgroundColor: '#3B82F6' }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        type={confirmModal.type}
      />
    </div>
  );
};

export default ChecklistItemWorkspace;

